import {diApi} from '@di-shared/api/di';
import {type ApiResponseGetRecentTripDaysResponse, type GetTripDaysResponse, Trip} from '@di-shared/api/di/__generated';
import {getYYYYMMFormat} from '@di-shared/api/di/utils';
import environmentStore from '@di-shared/features/environment/environmentStore';
import {VALID_MONTH_PERIOD} from '@di-shared/features/history/driving-info/useDrivingInfo.kr';
import {
  endDateOfMonth,
  isDateSameWeek,
  isSameYearAndMonth,
  parseDateWithFormat,
  setDay,
  startDateOfMonth,
  subDate,
  weekOfInterval,
} from '@shared/dateUtils';
import {getQueryKey} from '@shared/queryKeyMap';
import {useSuspenseQuery} from '@tanstack/react-query';

function getFocusTripDate(selectedDate: Date, tripDays: Array<Trip>) {
  const today = new Date();
  const isCurrentMonth = isSameYearAndMonth(today, selectedDate);
  const existTripDays = tripDays.filter((trip) => trip.tripCnt > 0);
  const firstTripDay = tripDays[0]?.day;

  // 주행 기록이 없는 경우
  // 현재 월: 오늘 날짜에 포커스 / 과거 월: 해당 월의 1일에 포커스
  if (existTripDays.length === 0) {
    if (isCurrentMonth) {
      return today;
    }

    return firstTripDay ? setDay(selectedDate, firstTripDay) : startDateOfMonth(selectedDate);
  }

  // 주행기록이 있는 경우
  // 현재 월: 마지막 주행의 주 및 일자에 포커스 / 과거 월: 최초 주행의 주 및 일자에 포커스
  return setDay(selectedDate, isCurrentMonth ? existTripDays[existTripDays.length - 1].day : existTripDays[0].day);
}

export function isTripOnDay(tripDays: Array<Trip>, day: number) {
  return tripDays.some((trip) => trip.day === day && trip.tripCnt > 0);
}

export function getWeeksOfMonth(date: Date) {
  const monthStart = startDateOfMonth(date);
  const monthEnd = endDateOfMonth(date);

  return weekOfInterval(monthStart, monthEnd);
}

export function getWeekIndexOfDate(date: Date) {
  return getWeeksOfMonth(date).findIndex((weekStart) => isDateSameWeek(date, weekStart));
}

function isSelectedMonthAfterRecentTripMonth(selectedDate: Date, recentTripDate: Date | null) {
  if (!recentTripDate) {
    return false;
  }

  return startDateOfMonth(selectedDate).getTime() > startDateOfMonth(recentTripDate).getTime();
}

function createEmptyTripDays(selectedMonth: string): GetTripDaysResponse {
  return {
    month: selectedMonth,
    trips: [],
  };
}

function getMinTripDate(selectedDate: Date, tripDays: Array<Trip>) {
  const minPeriodDate = startDateOfMonth(subDate(new Date(), {months: VALID_MONTH_PERIOD - 1}));
  const firstTripDay = tripDays[0]?.day;

  if (!firstTripDay || firstTripDay === 1) {
    return minPeriodDate;
  }

  return setDay(selectedDate, firstTripDay);
}

export default function useTripDayKr({vin, selectedDate}: {vin: string; selectedDate: Date}) {
  const ccId = environmentStore.getState().ccid;
  const selectedMonth = getYYYYMMFormat(selectedDate);

  const {data: recentTripDayData} = useSuspenseQuery({
    queryKey: [getQueryKey(diApi.driveHistory.getRecentTripDays1), vin],
    queryFn: () => diApi.driveHistory.getRecentTripDays1({vin}),
    select: ({data}: ApiResponseGetRecentTripDaysResponse) => data,
  });

  const verifiedRecentTripDate = parseDateWithFormat(recentTripDayData.recentTripDate);
  const isSelectedRecentTripMonth = verifiedRecentTripDate ? isSameYearAndMonth(verifiedRecentTripDate, selectedDate) : false;
  // recentTripDate가 선택된 월보다 과거 월이면 선택된 월에는 주행이 없으므로
  // /trip/days를 다시 호출하지 않고 빈 trips로 처리한다.
  // 선택된 월이 현재 월인 경우 today까지만 활성화되는 동작은 DatePicker의 maxDate=today가 담당한다.
  // 달력에서 선택 가능 범위는 minDate/maxDate가 결정하고, tripDays는 주행 dot 표시와 focus 계산에만 사용된다.
  const shouldUseEmptyTripDays = isSelectedMonthAfterRecentTripMonth(selectedDate, verifiedRecentTripDate);
  const tripDayQueryKey =
    isSelectedRecentTripMonth || shouldUseEmptyTripDays
      ? getQueryKey(diApi.driveHistory.getRecentTripDays1)
      : getQueryKey(diApi.driveHistory.getTripDays1);

  const {data: tripDayData} = useSuspenseQuery({
    queryKey: [tripDayQueryKey, vin, ccId, selectedMonth, shouldUseEmptyTripDays, isSelectedRecentTripMonth],
    queryFn: async (): Promise<GetTripDaysResponse> => {
      if (shouldUseEmptyTripDays) {
        return createEmptyTripDays(selectedMonth);
      }

      if (isSelectedRecentTripMonth) {
        return recentTripDayData.tripDays ?? createEmptyTripDays(selectedMonth);
      }

      // recentTripDate가 null이면 가입일을 알 수 없으므로 선택된 월의 /trip/days를 조회해
      // 가입일 이후 0건 응답을 포함한 실제 활성 범위를 확인한다.
      const {data} = await diApi.driveHistory.getTripDays1({
        vin,
        ccId,
        month: selectedMonth,
      });

      return data;
    },
  });

  const focusTripDate = getFocusTripDate(selectedDate, tripDayData.trips);
  const minDate = getMinTripDate(selectedDate, tripDayData.trips);

  return {
    tripDays: tripDayData.trips,

    minDate,
    focusTripDate,
  };
}
